import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, AlertCircle, CheckCircle2, HelpCircle, MinusCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { auditTemplateNoticePeriods } from '../api';

/**
 * Notice-Period Audit dialog.
 *
 * Renders the JSON from `GET /api/offer-letters/templates/notice-period-audit`
 * as a coloured, sortable report so HR can spot templates whose
 * `terms_and_conditions.notice_period` field contradicts the new tiered
 * policy from the Termination and Notice Period clause.
 *
 * Per-status colour coding lets HR scan a long list quickly:
 *   - mismatch       → red
 *   - unparseable    → amber
 *   - compliant      → green
 *   - not_applicable → slate (informational)
 */
const STATUS_META = {
  mismatch: {
    label: 'Mismatch',
    icon: AlertCircle,
    chipClass: 'bg-red-100 text-red-700 border-red-300',
    rowClass: 'border-l-4 border-red-400 bg-red-50/40',
  },
  unparseable: {
    label: 'Unparseable',
    icon: HelpCircle,
    chipClass: 'bg-amber-100 text-amber-700 border-amber-300',
    rowClass: 'border-l-4 border-amber-400 bg-amber-50/40',
  },
  compliant: {
    label: 'Compliant',
    icon: CheckCircle2,
    chipClass: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    rowClass: 'border-l-4 border-emerald-400 bg-emerald-50/40',
  },
  not_applicable: {
    label: 'N/A',
    icon: MinusCircle,
    chipClass: 'bg-slate-100 text-slate-600 border-slate-300',
    rowClass: 'border-l-4 border-slate-300 bg-slate-50/30',
  },
};

const NoticePeriodAuditDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const res = await auditTemplateNoticePeriods();
      setReport(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to load notice-period audit');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch the audit each time the dialog opens so HR always sees fresh
  // data after editing a template.
  useEffect(() => {
    if (open) {
      fetchAudit();
      setFilter('all');
    }
  }, [open]);

  const visibleRows = !report ? [] : (
    filter === 'all' ? report.templates : report.templates.filter(r => r.status === filter)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="notice-period-audit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle size={20} className="text-amber-600" />
            Notice-Period Audit
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7 px-2 text-xs"
              onClick={fetchAudit}
              disabled={loading}
              data-testid="notice-audit-refresh-btn"
            >
              <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Finds templates whose <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">terms_and_conditions.notice_period</code> field contradicts the new tiered policy from the Termination and Notice Period clause.
          </DialogDescription>
        </DialogHeader>

        {loading && !report && (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-slate-400" size={32} />
          </div>
        )}

        {report && (
          <>
            {/* Tiered-policy banner */}
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 text-xs text-indigo-900">
              <p className="font-semibold mb-1">Tiered policy (from the new clause):</p>
              <div className="grid grid-cols-2 gap-2">
                <div><strong>Tenured:</strong> {report.tiered_policy?.tenured}</div>
                <div><strong>Probation:</strong> {report.tiered_policy?.probation}</div>
              </div>
            </div>

            {/* Summary chips (also act as filter buttons) */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                ['all', `Total ${report.summary.total}`, 'bg-slate-100 text-slate-700 border-slate-300'],
                ['mismatch', `Mismatch ${report.summary.mismatch}`, STATUS_META.mismatch.chipClass],
                ['unparseable', `Unparseable ${report.summary.unparseable}`, STATUS_META.unparseable.chipClass],
                ['compliant', `Compliant ${report.summary.compliant}`, STATUS_META.compliant.chipClass],
                ['not_applicable', `N/A ${report.summary.not_applicable}`, STATUS_META.not_applicable.chipClass],
              ].map(([key, label, cls]) => (
                <button
                  key={key}
                  type="button"
                  className={`px-3 py-1.5 rounded border text-xs font-medium transition ${cls} ${filter === key ? 'ring-2 ring-offset-1 ring-indigo-400' : 'opacity-80 hover:opacity-100'}`}
                  onClick={() => setFilter(key)}
                  data-testid={`notice-audit-filter-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Results list */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {visibleRows.length === 0 ? (
                <div className="py-12 text-center text-sm text-slate-400">
                  No templates match the current filter.
                </div>
              ) : (
                visibleRows.map((row) => {
                  const meta = STATUS_META[row.status] || STATUS_META.unparseable;
                  const Icon = meta.icon;
                  return (
                    <div
                      key={row.id}
                      className={`rounded-md px-3 py-2.5 ${meta.rowClass}`}
                      data-testid={`notice-audit-row-${row.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <Icon size={16} className="mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-slate-900 truncate">{row.name || '—'}</span>
                            <Badge className={`text-[10px] py-0 px-1.5 ${meta.chipClass}`}>
                              {meta.label}
                            </Badge>
                            {row.category && (
                              <span className="text-[11px] text-slate-500">· {row.category}</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-700 mb-1">
                            <span className="text-slate-500">notice_period:</span>{' '}
                            <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[11px]">
                              {row.notice_period || '(empty)'}
                            </code>
                            {row.normalised_days != null && (
                              <span className="ml-2 text-slate-400">
                                ≈ {row.normalised_days} days
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-600 leading-relaxed">{row.note}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="notice-audit-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NoticePeriodAuditDialog;
